import { IonButton, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonNote, IonPage, IonRadio, IonRadioGroup, IonSelect, IonSelectOption, IonTitle, IonToolbar } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { checkmarkOutline } from 'ionicons/icons';
import { useState } from 'react';
import styles from '../DoorPolicyModal.module.css';
import { useCommonTranslation, useModalTranslation } from '../../hooks/useTranslation';
import { type DurationUnit, getDurationMs, getDurationParts } from '../../utils/doorPolicyTranslations';

export type LockActionOption = 'none' | 'lock' | 'unlock';
export type LockDurationOption = 'default' | 'until_event_end' | 'for_duration';

export interface EditDoorPolicyLockPickerProps {
    lockActionOption: LockActionOption;
    lockDurationOption: LockDurationOption;
    lockDurationMs?: number;
    onSelectLock: (option: LockActionOption, durationOption: LockDurationOption, lockDurationMs?: number) => void;
    onClose: () => void;
}

const DEFAULT_DURATION_VALUE = 30;
const MAX_DURATION_VALUE = 59;
const DEFAULT_DURATION_UNIT: DurationUnit = 'seconds';

const getInitialDurationState = (lockDurationMs?: number) => {
    if (lockDurationMs && lockDurationMs > 0) {
        const { value, unit } = getDurationParts(lockDurationMs);
        if (unit === 'hours') {
            const minutesValue = Math.min(value * 60, MAX_DURATION_VALUE);
            return { value: String(minutesValue), unit: 'minutes' as const };
        }
        // The lockout input is whole-valued; round fractional seconds up to at least 1.
        const wholeValue = Math.max(1, Math.round(value));
        return { value: String(Math.min(wholeValue, MAX_DURATION_VALUE)), unit };
    }
    return { value: String(DEFAULT_DURATION_VALUE), unit: DEFAULT_DURATION_UNIT };
};

const normalizeDurationValue = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return String(DEFAULT_DURATION_VALUE);
    }
    return String(Math.min(Math.max(parsed, 1), MAX_DURATION_VALUE));
};

const EditDoorPolicyLockPicker = ({
    lockActionOption,
    lockDurationOption,
    lockDurationMs,
    onSelectLock,
    onClose,
}: EditDoorPolicyLockPickerProps) => {
    const [selectedLock, setSelectedLock] = useState(lockActionOption);
    const [selectedDurationOption, setSelectedDurationOption] = useState<LockDurationOption>(lockDurationOption);
    const initialDurationState = getInitialDurationState(lockDurationMs);
    const [durationValue, setDurationValue] = useState(initialDurationState.value);
    const [durationUnit, setDurationUnit] = useState<DurationUnit>(initialDurationState.unit);
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const lockOptions: Array<{ id: LockActionOption; label: string }> = [
        { id: 'none', label: tModals('door_policy.pickers.lock.do_nothing') },
        { id: 'unlock', label: tModals('door_policy.pickers.lock.unlock') },
        { id: 'lock', label: tModals('door_policy.pickers.lock.lock') },
    ];
    const durationOptions: Array<{ id: LockDurationOption; label: string }> = [
        { id: 'default', label: tModals('door_policy.pickers.lock.duration_default') },
        { id: 'until_event_end', label: tModals('door_policy.pickers.lock.duration_until_event_end') },
        { id: 'for_duration', label: tModals('door_policy.pickers.lock.duration_for_duration') },
    ];

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={onClose}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    {/* ios title mode keeps the title centered in md too */}
                    <IonTitle {...({ mode: 'ios' } as object)}>{tModals('door_policy.pickers.lock.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            strong={true}
                            onClick={() => {
                                if (selectedLock === 'lock' && selectedDurationOption === 'for_duration') {
                                    const normalizedValue = normalizeDurationValue(durationValue);
                                    const durationMsValue = getDurationMs(Number(normalizedValue), durationUnit);
                                    onSelectLock(selectedLock, selectedDurationOption, durationMsValue);
                                } else {
                                    onSelectLock(selectedLock, selectedDurationOption);
                                }
                                onClose();
                            }}
                        >
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <section className={`${styles.wrapper} ${styles.section}`}>
                    <h2 className={styles.sectionLabel}>{tModals('door_policy.pickers.lock.heading')}</h2>
                    <IonList className={styles.listCard}>
                        {lockOptions.map((option) => (
                            <IonItem
                                key={option.id}
                                button
                                detail={false}
                                onClick={() => {
                                    setSelectedLock(option.id);
                                }}
                            >
                                <IonLabel>{option.label}</IonLabel>
                                <IonIcon
                                    slot="end"
                                    icon={checkmarkOutline}
                                    className={`${styles.pickerCheckIcon} ${selectedLock === option.id ? styles.pickerCheckIconActive : ''}`}
                                />
                            </IonItem>
                        ))}
                    </IonList>
                    {selectedLock === 'lock' && (
                        <>
                            <h2 className={`${styles.sectionLabel} ${styles.sectionLabelSpaced}`}>
                                {tModals('door_policy.pickers.lock.duration_heading')}
                            </h2>
                            <IonList className={styles.listCard}>
                                <IonRadioGroup
                                    value={selectedDurationOption}
                                    onIonChange={(event) => {
                                        const nextOption = event.detail.value as LockDurationOption;
                                        setSelectedDurationOption(nextOption);
                                        if (nextOption === 'for_duration') {
                                            setDurationValue(normalizeDurationValue(durationValue));
                                        }
                                    }}
                                >
                                    {durationOptions.map((option) => (
                                        <IonItem key={option.id}>
                                            <IonRadio value={option.id} labelPlacement="end" justify="start">
                                                <div className={styles.durationOption}>
                                                    <div>{option.label}</div>
                                                    {option.id === 'default' && (
                                                        <IonNote className={styles.durationNote}>
                                                            {tModals('door_policy.pickers.lock.duration_note')}
                                                        </IonNote>
                                                    )}
                                                </div>
                                            </IonRadio>
                                        </IonItem>
                                    ))}
                                </IonRadioGroup>
                                {selectedDurationOption === 'for_duration' && (
                                    <>
                                        <IonItem>
                                            <IonLabel position="stacked">
                                                {tModals('door_policy.pickers.lock.duration_label')}
                                            </IonLabel>
                                            <IonInput
                                                type="number"
                                                inputmode="numeric"
                                                min="1"
                                                max={String(MAX_DURATION_VALUE)}
                                                value={durationValue}
                                                onIonInput={(event) => setDurationValue(event.detail.value ?? '')}
                                                onIonBlur={() => setDurationValue(normalizeDurationValue(durationValue))}
                                            />
                                        </IonItem>
                                        <IonItem>
                                            <IonLabel position="stacked">
                                                {tModals('door_policy.pickers.lock.duration_unit_label')}
                                            </IonLabel>
                                            <IonSelect
                                                value={durationUnit}
                                                interface="popover"
                                                onIonChange={(event) => setDurationUnit(event.detail.value as DurationUnit)}
                                            >
                                                <IonSelectOption value="seconds">
                                                    {tModals('door_policy.duration_units.seconds')}
                                                </IonSelectOption>
                                        <IonSelectOption value="minutes">
                                            {tModals('door_policy.duration_units.minutes')}
                                        </IonSelectOption>
                                    </IonSelect>
                                </IonItem>
                            </>
                        )}
                    </IonList>
                </>
            )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export const EditDoorPolicyLockPickerView = (props: EditDoorPolicyLockPickerProps) => (
    <EditDoorPolicyLockPicker {...props} />
);

export default EditDoorPolicyLockPicker;
